import React from 'react';

/**
 * Renders the evidence the model cited. Two kinds:
 *   - table  → row list from one of (cmps, pm_spans, root_causes)
 *   - insight → one of the authored root-cause narratives
 *
 * Intentionally minimal rendering: the goal is visible provenance, not a
 * pretty chart. If rows are an array of plain objects, we render a small
 * HTML table. Unknown shapes render as formatted JSON so nothing hides.
 */

const TABLE_LABELS = {
  cmps: 'CMPs · Fiber CM (Feb 2026)',
  pm_spans: 'PM Spans · Fiber PM (Feb 2026)',
  root_causes: 'Root Causes · Fiber, Feb 2026 zone-wide',
  circles: 'Circles · Feb 2026 rollups',
  zones: 'North Zone · Feb 2026',
  penalties: 'Penalties · Feb 2026 (tiers only, ₹ pending)',
  tower_cm: 'Tower CM · Feb 2026 (14 CMPs)',
  tower_pm: 'Tower PM · Feb 2026 (14 CMPs)',
  tower_exec: 'Tower Executive · Feb 2026',
  misc_cm: 'Misc CM (OSC/AG1/MFL) · Feb 2026',
  misc_pm: 'Misc PM (OSC/AG1/MFL) · Feb 2026',
  misc_exec: 'Misc Executive · Feb 2026',
  manpower: 'Manpower Gap · Feb 2026',
  fiber_exec: 'Fiber Executive Overview · Dec 2025 (₹ waterfall)',
};

export default function SourceCard({ evidence }) {
  if (!evidence) return null;

  if (evidence.kind === 'table') {
    return (
      <div
        data-drishti="source-card"
        data-kind="table"
        className="rounded-lg border border-app-border bg-slate-50 p-3"
      >
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          {TABLE_LABELS[evidence.table] || evidence.table}
        </div>
        <RowsTable rows={evidence.rows} />
      </div>
    );
  }

  if (evidence.kind === 'insight') {
    return (
      <div
        data-drishti="source-card"
        data-kind="insight"
        className="rounded-lg border border-app-border bg-violet-50/60 p-3"
      >
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-violet-700">
          Insight
        </div>
        <div className="text-sm font-medium text-slate-800">
          {evidence.title}
        </div>
        {evidence.rule ? (
          <div className="mt-1 font-mono text-xs text-slate-600">
            Rule: {evidence.rule}
          </div>
        ) : null}
      </div>
    );
  }

  return null;
}

function RowsTable({ rows }) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return (
      <div className="text-xs italic text-slate-500">
        No rows attached.
      </div>
    );
  }

  const first = rows[0];
  const isObjectShape =
    first && typeof first === 'object' && !Array.isArray(first);

  if (!isObjectShape) {
    return (
      <pre className="overflow-x-auto font-mono text-xs text-slate-700">
        {JSON.stringify(rows, null, 2)}
      </pre>
    );
  }

  const columns = Array.from(
    new Set(rows.flatMap((r) => Object.keys(r || {}))),
  );

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs">
        <thead>
          <tr className="text-left text-slate-500">
            {columns.map((c) => (
              <th key={c} className="px-2 py-1 font-semibold">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-app-border text-slate-700">
          {rows.slice(0, 20).map((row, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c} className="px-2 py-1 font-mono">
                  {formatCell(row?.[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 20 ? (
        <div className="mt-1 text-xs italic text-slate-500">
          Showing first 20 of {rows.length}.
        </div>
      ) : null}
    </div>
  );
}

function formatCell(value) {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  return String(value);
}
